/**
 * TUVI - Tipos de Proveedor CRUD
 * Administración de tipos de proveedor
 */

import React, { useState, useEffect } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './TiposProveedor.css';

const TiposProveedor = () => {
  const { success, error, warning, confirm } = useNotification();
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    color: '#607d8b',
    descripcion: ''
  });
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    cargarTipos();
  }, []);

  const cargarTipos = async () => {
    try {
      setLoading(true);
      const data = await client.query(`{ tiposProveedor { id codigo nombre color descripcion activo } }`);
      setTipos(data.tiposProveedor || []);
    } catch (err) {
      console.error('Error al cargar tipos:', err);
      error('Error al cargar tipos de proveedor');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      color: '#607d8b',
      descripcion: ''
    });
    setEditando(null);
    setMostrarFormulario(false);
  };

  const handleEditar = (tipo) => {
    setEditando(tipo);
    setFormData({
      nombre: tipo.nombre,
      color: tipo.color || '#607d8b',
      descripcion: tipo.descripcion || ''
    });
    setMostrarFormulario(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      warning('El nombre es requerido');
      return;
    }

    setGuardando(true);

    try {
      let data;
      const inputData = { codigo: formData.nombre.toLowerCase().replace(/\s+/g, '_'), nombre: formData.nombre, color: formData.color, descripcion: formData.descripcion };

      if (editando) {
        data = await client.mutate(`mutation($id: Int!, $input: TipoProveedorInput!) { actualizarTipoProveedor(id: $id, input: $input) { success message } }`, { id: editando.id, input: inputData });
        data = data.actualizarTipoProveedor;
      } else {
        data = await client.mutate(`mutation($input: TipoProveedorInput!) { crearTipoProveedor(input: $input) { success message } }`, { input: inputData });
        data = data.crearTipoProveedor;
      }

      if (data.success) {
        success(editando ? 'Tipo actualizado exitosamente' : 'Tipo creado exitosamente');
        resetForm();
        cargarTipos();
      } else {
        error('Error: ' + data.message);
      }
    } catch (err) {
      console.error('Error al guardar:', err);
      error('Error al guardar el tipo');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (tipo) => {
    if (tipo.cantidad_proveedores > 0) {
      warning(`No se puede eliminar: hay ${tipo.cantidad_proveedores} proveedor(es) usando este tipo.`);
      return;
    }

    const confirmed = await confirm({
      title: 'Eliminar tipo de proveedor',
      message: `¿Está seguro de eliminar el tipo "${tipo.nombre}"?`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      const gqlData = await client.mutate(`mutation($id: Int!) { eliminarTipoProveedor(id: $id) { success message } }`, { id: tipo.id });

      if (gqlData.eliminarTipoProveedor.success) {
        success('Tipo eliminado exitosamente');
        cargarTipos();
      } else {
        error('Error: ' + gqlData.eliminarTipoProveedor.message);
      }
    } catch (err) {
      console.error('Error al eliminar:', err);
      error('Error al eliminar el tipo');
    }
  };

  const coloresPredefinidos = [
    '#ff9800', '#2196f3', '#4caf50', '#9c27b0',
    '#f44336', '#607d8b', '#00bcd4', '#e91e63',
    '#3f51b5', '#009688', '#795548', '#ff5722'
  ];

  if (loading) {
    return (
      <div className="tipos-proveedor-loading">
        <div className="construction-loader">
          <div className="loader-bricks">
            <div className="loader-brick"></div>
            <div className="loader-brick"></div>
            <div className="loader-brick"></div>
          </div>
        </div>
        <p>Cargando tipos de proveedor...</p>
      </div>
    );
  }

  return (
    <div className="tipos-proveedor">
      {/* Header */}
      <div className="tipos-header">
        <div className="header-left">
          <div className="header-text">
            <h2>Tipos de Proveedores</h2>
            <p>Administre las categorías de proveedores</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="btn-nuevo"
            onClick={() => {
              resetForm();
              setMostrarFormulario(!mostrarFormulario);
            }}
          >
            {mostrarFormulario ? 'Cancelar' : 'Nuevo Tipo'}
          </button>
        </div>
      </div>

      {/* Formulario */}
      {mostrarFormulario && (
        <div className="form-tipo">
          <h3>{editando ? 'Editar Tipo' : 'Nuevo Tipo de Proveedor'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Nombre *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  placeholder="Ej: Materiales de Construcción"
                  required
                />
              </div>
              <div className="form-group">
                <label>Color</label>
                <div className="color-picker-container">
                  <input
                    type="color"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    className="color-input"
                  />
                  <div className="colores-rapidos">
                    {coloresPredefinidos.map(color => (
                      <button
                        key={color}
                        type="button"
                        className={`color-btn ${formData.color === color ? 'active' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData(prev => ({ ...prev, color }))}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>Descripción (opcional)</label>
              <textarea
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
                placeholder="Descripción del tipo de proveedor..."
                rows={2}
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancelar" onClick={resetForm}>
                Cancelar
              </button>
              <button type="submit" className="btn-guardar" disabled={guardando}>
                {guardando ? 'Guardando...' : (editando ? 'Actualizar' : 'Crear Tipo')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Tipos */}
      <div className="tipos-lista">
        {tipos.length === 0 ? (
          <div className="empty-state">
            <h3>No hay tipos de proveedor</h3>
            <p>Cree un nuevo tipo para comenzar</p>
          </div>
        ) : (
          <div className="tipos-grid">
            {tipos.map(tipo => (
              <div key={tipo.id} className="tipo-card">
                <div className="tipo-header">
                  <div
                    className="tipo-color"
                    style={{ backgroundColor: tipo.color || '#607d8b' }}
                  />
                  <div className="tipo-info">
                    <h4>{tipo.nombre}</h4>
                    <span className="tipo-codigo">{tipo.codigo}</span>
                  </div>
                </div>
                {tipo.descripcion && (
                  <p className="tipo-descripcion">{tipo.descripcion}</p>
                )}
                <div className="tipo-footer">
                  <span className="tipo-proveedores">
                    {tipo.cantidad_proveedores || 0} proveedor(es)
                  </span>
                  <div className="tipo-acciones">
                    <button
                      className="btn-editar"
                      onClick={() => handleEditar(tipo)}
                      title="Editar"
                    >
                      Editar
                    </button>
                    <button
                      className="btn-eliminar"
                      onClick={() => handleEliminar(tipo)}
                      disabled={tipo.cantidad_proveedores > 0}
                      title={tipo.cantidad_proveedores > 0 ? 'No se puede eliminar: tiene proveedores asignados' : 'Eliminar'}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TiposProveedor;
